import { useState } from 'react';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_FORMATTING = /[\s\-()+.]/g;
const sanitize = (val) => val.trim().replace(/\s+/g, ' ');

const EMPTY_FORM = { name: '', email: '', phone: '', agency: '', industry: '', message: '' };

const useContactForm = (onSuccess, pageName) => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const sanitized = {
      name: sanitize(formData.name),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      agency: sanitize(formData.agency),
      industry: sanitize(formData.industry),
      message: formData.message.trim(),
    };

    const newErrors = {};
    if (!sanitized.name) newErrors.name = 'Looks like you missed this';
    if (!sanitized.email) {
      newErrors.email = 'Looks like you missed this';
    } else if (!EMAIL_REGEX.test(sanitized.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (sanitized.phone) {
      const digits = sanitized.phone.replace(PHONE_FORMATTING, '');
      if (!/^\d+$/.test(digits) || digits.length < 7 || digits.length > 15) {
        newErrors.phone = 'Please enter a valid phone number';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      window.adobeDataLayer = window.adobeDataLayer || [];
      window.adobeDataLayer.push({
        event: 'RIOTT_FORM_SUBMITTED',
        page: {
          name: pageName,
        },
        form_data: {
          company: sanitized.agency,
          emailAddress: sanitized.email,
          firstName: sanitized.name.split(' ')[0],
          lastName: sanitized.name.split(' ').slice(1).join(' '),
          industry: sanitized.industry,
          message: sanitized.message,
          phone: sanitized.phone,
        },
      });

      setShowSuccessMessage(true);
      setFormData(EMPTY_FORM);
      onSuccess?.();
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setErrors({});
    setSubmitError('');
    setShowSuccessMessage(false);
  };

  return {
    formData,
    errors,
    isSubmitting,
    submitError,
    showSuccessMessage,
    setShowSuccessMessage,
    handleChange,
    handleSubmit,
    resetForm,
  };
};

export default useContactForm;
